import { Avatar } from "@components/ui/Avatar"
import { Card, CardBody } from "@components/ui/Card"
import { useGradeScale } from "@hooks/useGradeScale"
import { cn } from "@lib/cn"
import { formatGrade, formatPercent, formatRelativeDate } from "@lib/intl"
import { ArrowUpDown, Minus, TrendingDown, TrendingUp } from "lucide-react"
import { memo, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import type { DirectionFacultyRow, FacultyStatus, Trend } from "../types"

import { gradeTier, gradeTierTextClass } from "@/constants/gradeTier"
import type { GradeScale } from "@/types"

interface Props {
  rows: DirectionFacultyRow[]
}

type SortKey = "name" | "students" | "avg_grade" | "delivery_rate" | "at_risk_count"
type SortDir = "asc" | "desc"

const STATUS_TONE: Record<FacultyStatus, string> = {
  strong: "bg-success-soft text-success-ink",
  watch: "bg-attention-soft text-attention-ink",
  concern: "bg-danger-soft text-danger-ink",
}

const STATUS_DOT: Record<FacultyStatus, string> = {
  strong: "bg-success",
  watch: "bg-attention",
  concern: "bg-danger",
}

// Faculty rows show *a teacher's* portfolio average — use the
// stricter "group" tier the same as the at-a-glance dashboards.
function gradeColor(avg: number, scale: GradeScale) {
  return gradeTierTextClass(gradeTier(avg, scale, "group"))
}

function deliveryColor(rate: number) {
  return gradeTierTextClass(gradeTier(rate, "0-100", "group"))
}

function TrendIcon({ trend }: { trend: Trend }) {
  if (trend === "improving") return <TrendingUp className="text-success h-3.5 w-3.5" />
  if (trend === "declining") return <TrendingDown className="text-danger-ink h-3.5 w-3.5" />
  return <Minus className="text-ink-subtle h-3.5 w-3.5" />
}

export const FacultyTable = memo(function FacultyTable({ rows }: Props) {
  const { t } = useTranslation("direction")
  const scale = useGradeScale()
  const [sortKey, setSortKey] = useState<SortKey>("at_risk_count")
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  const sorted = useMemo(() => {
    const copy = [...rows]
    copy.sort((a, b) => {
      const av = sortKey === "name" ? a.name : a[sortKey]
      const bv = sortKey === "name" ? b.name : b[sortKey]
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av)
      }
      return sortDir === "asc" ? Number(av) - Number(bv) : Number(bv) - Number(av)
    })
    return copy
  }, [rows, sortKey, sortDir])

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDir(key === "name" ? "asc" : "desc")
    }
  }

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-ink text-sm font-semibold">{t("faculty.title")}</h2>
          <span className="text-ink-subtle text-xs">
            {t("faculty.count", { count: rows.length })}
          </span>
        </div>

        {/* Desktop / tablet table */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-ink-subtle text-left text-xs uppercase">
                <SortableHeader
                  active={sortKey === "name"}
                  dir={sortDir}
                  onClick={() => toggleSort("name")}
                  label={t("faculty.col.name")}
                />
                <th className="px-2 py-2 font-medium">{t("faculty.col.classrooms")}</th>
                <SortableHeader
                  active={sortKey === "students"}
                  dir={sortDir}
                  onClick={() => toggleSort("students")}
                  label={t("faculty.col.students")}
                />
                <SortableHeader
                  active={sortKey === "avg_grade"}
                  dir={sortDir}
                  onClick={() => toggleSort("avg_grade")}
                  label={t("faculty.col.avg_grade")}
                />
                <SortableHeader
                  active={sortKey === "delivery_rate"}
                  dir={sortDir}
                  onClick={() => toggleSort("delivery_rate")}
                  label={t("faculty.col.delivery")}
                />
                <SortableHeader
                  active={sortKey === "at_risk_count"}
                  dir={sortDir}
                  onClick={() => toggleSort("at_risk_count")}
                  label={t("faculty.col.at_risk")}
                />
                <th className="px-2 py-2 font-medium">{t("faculty.col.last_sent")}</th>
                <th className="px-2 py-2 font-medium">{t("faculty.col.status")}</th>
              </tr>
            </thead>
            <tbody className="divide-hairline divide-y">
              {sorted.map((row) => (
                // PRD-59 — no `onClick`, no `cursor-pointer`. The row used to
                // carry both, plus a hover tint, and the handler behind them
                // only wrote a log line: there is no per-professor page for a
                // director to land on, and there is not meant to be one
                // (`boundaries.md` — the director realm reads institution
                // aggregates, never a professor's roster). So the affordance
                // promised a destination that does not exist, and a keyboard
                // could not reach it anyway. The hover stays as row tracking,
                // which is what a wide table actually needs.
                <tr key={row.professor_id} className="hover:bg-inset transition-colors">
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <Avatar name={row.name} initials={row.initials} color={row.color} size="sm" />
                      <div className="min-w-0">
                        <p className="text-ink truncate font-medium">{row.name}</p>
                        <p className="text-ink-subtle truncate text-xs">{row.primary_subject}</p>
                      </div>
                    </div>
                  </td>
                  <td className="text-ink-muted px-2 py-2 tabular-nums">{row.classrooms}</td>
                  <td className="text-ink-muted px-2 py-2 tabular-nums">{row.students}</td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-1.5">
                      <span
                        className={cn(
                          "font-semibold tabular-nums",
                          gradeColor(row.avg_grade, scale),
                        )}
                      >
                        {formatGrade(row.avg_grade, scale)}
                      </span>
                      <TrendIcon trend={row.trend} />
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <span
                      className={cn("font-semibold tabular-nums", deliveryColor(row.delivery_rate))}
                    >
                      {formatPercent(row.delivery_rate)}
                    </span>
                  </td>
                  <td className="px-2 py-2">
                    <span
                      className={cn(
                        "tabular-nums",
                        row.at_risk_count > 0 ? "text-danger-ink" : "text-ink-subtle",
                      )}
                    >
                      {row.at_risk_count}
                    </span>
                  </td>
                  <td className="text-ink-subtle px-2 py-2 text-xs">
                    {row.last_material_sent_at
                      ? formatRelativeDate(row.last_material_sent_at)
                      : t("faculty.never_sent")}
                  </td>
                  <td className="px-2 py-2">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
                        STATUS_TONE[row.status],
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT[row.status])}
                      />
                      {t(`faculty.status.${row.status}`)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile stacked cards */}
        <ul className="space-y-2 md:hidden">
          {sorted.map((row) => (
            <li key={row.professor_id}>
              {/* A container, not a button: same reason as the desktop row —
                  the handler behind it only logged, and there is no
                  per-professor destination in the director realm. */}
              <div className="rounded-control border-border bg-surface flex w-full items-start gap-3 border p-3 text-left">
                <Avatar name={row.name} initials={row.initials} color={row.color} size="sm" />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-ink truncate text-sm font-medium">{row.name}</p>
                    <span
                      className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
                        STATUS_TONE[row.status],
                      )}
                    >
                      {t(`faculty.status.${row.status}`)}
                    </span>
                  </div>
                  <p className="text-ink-subtle text-xs">
                    {row.primary_subject} ·{" "}
                    {t("faculty.mobile.classes_students", {
                      classes: row.classrooms,
                      students: row.students,
                    })}
                  </p>
                  <div className="flex items-center gap-3 text-xs">
                    <span
                      className={cn("font-semibold tabular-nums", gradeColor(row.avg_grade, scale))}
                    >
                      {formatGrade(row.avg_grade, scale)}
                    </span>
                    <span className={cn("tabular-nums", deliveryColor(row.delivery_rate))}>
                      {formatPercent(row.delivery_rate)}
                    </span>
                    {row.at_risk_count > 0 && (
                      <span className="text-danger-ink tabular-nums">
                        {t("faculty.mobile.at_risk", { count: row.at_risk_count })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
})

function SortableHeader({
  label,
  active,
  dir,
  onClick,
}: {
  label: string
  active: boolean
  dir: SortDir
  onClick: () => void
}) {
  return (
    <th className="px-2 py-2 font-medium">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs uppercase transition-colors",
          "focus:ring-2 focus:outline-none",
          active ? "text-ink" : "text-ink-subtle hover:text-ink-muted",
        )}
      >
        <span>{label}</span>
        <ArrowUpDown
          className={cn("h-3 w-3 transition-transform", active && dir === "asc" && "rotate-180")}
          aria-hidden="true"
        />
      </button>
    </th>
  )
}
